/**
 * Copyright 2025 (c) Karsten Röthig, FVA GmbH (for umati and VDW e.V.)
 */
import { checkUrl } from '@umati/dashboard-common';
import SectionData from 'components/machine/sectionData';
import React, { ReactElement } from 'react';
import { TemplateContext } from 'templates/pubSub/types/TemplateContextType';
import { SectionRowElementText } from 'templates/pubSub/types/TemplateType';
import { NodeIdentifier } from 'utilities/PubSub/PubSubAddressSpace';
import { usePubSubNode, usePubSubValue } from 'utilities/PubSub/PubSubHooks';
import TemplateHelperPubSub from 'utilities/TemplateHelperPubSub';

interface ISectionElementContentTextProps {
  templateContext: TemplateContext;
  element: SectionRowElementText;
  cols: string;
  startingNodeId?: NodeIdentifier;
}

export default function SectionElementContentText(props: ISectionElementContentTextProps): ReactElement | null {
  const valueFilters = TemplateHelperPubSub.createFilter(
    props.templateContext.namespaces,
    props.element.valueVariable?.variablePath,
  );
  const valueNode = usePubSubNode(valueFilters, props.startingNodeId);
  const value = usePubSubValue(valueNode);

  const labelFilters = TemplateHelperPubSub.createFilter(
    props.templateContext.namespaces,
    props.element.labelVariable?.variablePath,
  );
  const labelNode = usePubSubNode(labelFilters, props.startingNodeId);
  const resolvedLabel = usePubSubValue(labelNode);
  const label = resolvedLabel ?? props.element.label;

  let unitSymbol: string | undefined;
  const unitFilters = TemplateHelperPubSub.createFilter(
    props.templateContext.namespaces,
    props.element.valueVariable?.hasUnit ? props.element.valueVariable?.variablePath + '.EngineeringUnits' : undefined,
  );
  const unitNode = usePubSubNode(unitFilters, props.startingNodeId);
  const unitValue = usePubSubValue(unitNode);
  if (unitValue) {
    unitSymbol = unitValue.DisplayName;
  }
  const displayValue = TemplateHelperPubSub.formatVariableValue(value, props.element.valueVariable, unitSymbol);

  if (
    TemplateHelperPubSub.shouldHideOptionalVariable(
      value,
      props.element.valueVariable,
      `SectionElementContentText:${props.element.id}`,
    )
  ) {
    return null;
  }

  return (
    <SectionData cols={props.cols} label={label}>
      {props.element.showAsLink && checkUrl(displayValue) ? (
        <a href={displayValue}>{displayValue}</a>
      ) : (
        <>{displayValue}</>
      )}
    </SectionData>
  );
}
